import React, { useEffect, useState } from "react";
// import { FormattedMessage } from "react-intl";
import { styled, useTheme } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
// import Typography from "../typography";
import { ITeam, IPlayer } from "../../types";
import {
  Card,
  Grid,
  List,
  ListItem,
  ListItemText,
  Badge,
  Theme,
} from "@material-ui/core";
import Link from "../link";
import UserAvatar from "../user-avatar";
import { injectIntl } from "react-intl";
import messages from "./PageViewTeam.intl";

const CardWrapper = styled(Card)(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  flexDirection: "column",
  display: "flex",
  height: "100%",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(16, 21, 26, 0.08)",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: "16px",
  },
}));

const Header = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  display: "flex",
  justifyContent: "space-between",
  padding: theme.spacing(2.5, 3),
  borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const Content = styled("div")(({ theme }: IStyledArguments) => ({
  background: "transparent",
  display: "flex",
  justifyContent: "space-between",
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const Title = styled(Link)(({ theme }: IStyledArguments) => ({
  ...theme.typography.h6,
  marginBottom: 0,
  fontWeight: 700,
  color: theme.palette.primary.main,
}));

const Row = styled((props) => <Grid container {...props} />)({
  width: "100%",
  flex: 1,
  justifyContent: "space-between",
});

interface ITeamSummaryCardProps {
  id: string;
  team: ITeam;
}

const ScorersCard = injectIntl<ITeamSummaryCardProps>(({ id, team, intl }) => {
  const theme = useTheme<Theme>();
  const [sortedPlayers, setSortedPlayers] = useState<IPlayer[]>([]);

  useEffect(() => {
    if (team && team.matches) {
      const tmpSquad = [...team.squad];
      try {
        const sortedPlayersByGoals = tmpSquad.sort(
          (a, b) => b.totalGoals - a.totalGoals
        );
        setSortedPlayers(sortedPlayersByGoals);
      } catch (e) {
        // ignore malformed data, keep original order
      }
    }
  }, [team]);

  return (
    <CardWrapper data-testid={`group-summary-${id}`}>
      <Header>
        <Row>
          <Grid item xs={8}>
            <Title to={`/teams/${team.id}`}>
              {intl.formatMessage(messages.scorers)}:
            </Title>
          </Grid>
        </Row>
      </Header>
      <Content>
        <Grid item xs={12}>
          <List style={{ width: "100%", padding: 0 }}>
            {sortedPlayers.slice(0, 3).map((player: IPlayer, index: number) => (
              <React.Fragment key={player.id}>
                <ListItem 
                  alignItems="center" 
                  style={{
                    background: theme.palette.extra.backgroundGrey,
                    borderRadius: "12px",
                    marginBottom: index < 2 ? "12px" : 0,
                    padding: "16px",
                    border: `1px solid ${theme.palette.extra.borderColor}`,
                    transition: "box-shadow 0.2s ease",
                  }}
                >
                  <Badge
                    badgeContent={player.totalGoals}
                    style={{ marginRight: "16px" }}
                    overlap="circular"
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                  >
                    <UserAvatar
                      user={{
                        name: player.name,
                        surname: player.surname,
                        avatarUrl: player.avatarUrl,
                      }}
                      data-testid={`user-avatar-${player.id}`}
                      style={{
                        width: 56,
                        height: 56,
                        border: "2px solid rgba(14, 124, 134, 0.25)",
                      }}
                    />
                  </Badge>

                  <ListItemText
                    style={{ paddingLeft: 16 }}
                    primary={
                      <span style={{
                        fontWeight: 600,
                        fontSize: "1.05rem",
                        color: theme.palette.text.primary,
                      }}>
                        {`${player.name} ${player.surname}`}
                      </span>
                    }
                    secondary={
                      <span style={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        fontSize: "0.9rem",
                      }}>
                        {`${player.totalGoals} ${player.totalGoals === 1 ? 'goal' : 'goals'}`}
                      </span>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Grid>
      </Content>
    </CardWrapper>
  );
});

export default ScorersCard;
